import omit from 'lodash/omit';

import { BLACKLISTED_PARAMS, layoutRegex } from '../constants';
import { LAYOUTS, FILTERS } from '../../../constants';

/**
 * Merges query parameters from different sources while handling real-time URL changes.
 *
 * This function addresses a specific issue where static queryParams (from server-side rendering)
 * may contain outdated polygon_location information that doesn't match the current browser URL.
 *
 *
 * @param {URL} currentUrl - The target URL object to modify
 * @param {Object} queryParams - Static query parameters (potentially from server)
 * @returns {URL} Modified URL with merged and cleaned query parameters
 */
const mergeQueryParams = (currentUrl, queryParams) => {
  const newUrl = Object.assign(currentUrl);

  const oldQuery = newUrl.search;

  let urlParams;
  let oldQueryObj = {};

  try {
    urlParams = new URLSearchParams(oldQuery);
    oldQueryObj = Object.fromEntries(urlParams);
  } catch (error) {
    oldQueryObj = {};
  }

  // URL Intervention for Real-time Query Parameters:
  // Check if we need to use current browser URL search params to handle cases
  // where static queryParams may contain outdated information (e.g., removed polygon)
  let currentQueryParams = queryParams;

  if (typeof window !== 'undefined' && window.location?.search && queryParams) {
    try {
      const browserSearchParams = new URLSearchParams(window.location.search);
      const querySearchParams = new URLSearchParams(queryParams);

      const hasPolygonInQuery = querySearchParams.has(FILTERS.IDS.POLYGON_LOCATION);
      const hasPolygonInBrowser = browserSearchParams.has(FILTERS.IDS.POLYGON_LOCATION);

      // Only use browser params if there's a mismatch in polygon_location presence
      // This ensures reactivity when polygon is removed while preserving other behaviors
      if (hasPolygonInQuery && !hasPolygonInBrowser) {
        const { [FILTERS.IDS.POLYGON_LOCATION]: _, ...restQueryParams } = queryParams;

        currentQueryParams = { ...restQueryParams, ...Object.fromEntries(browserSearchParams) };
      }
    } catch (error) {
      currentQueryParams = queryParams;
    }
  }

  const newQuery = { ...currentQueryParams, ...oldQueryObj };
  const params = omit(newQuery, BLACKLISTED_PARAMS);

  Object.keys(params).forEach((key) => {
    if (!newUrl.searchParams.has(key)) {
      newUrl.searchParams.append(key, params[key]);
    }
  });

  return newUrl;
};

const updateLayoutPath = (currentUrl, currentLayout, initialLayout) => {
  const newUrl = Object.assign(currentUrl);

  if (currentLayout && currentLayout.id !== initialLayout) {
    const oldLayout = layoutRegex.exec(newUrl.pathname);
    const newLayout = currentLayout.url_path?.match(layoutRegex);

    if (oldLayout) {
      newUrl.pathname = newUrl.pathname.replace(oldLayout[0], newLayout);
    } else {
      newUrl.pathname += newLayout;
    }
  }

  return newUrl;
};

/**
 * Handles polygon location parameter preservation in URLs.
 *
 * When users draw a polygon on the map and then navigate to other links,
 * we need to preserve the polygon_location parameter to maintain the search context.
 * This ensures that the polygon search remains active across navigation.
 *
 * URL Intervention Logic:
 * - Only applies when current layout is MAP view
 * - Extracts polygon_location from current URL search params
 * - Injects it into the target URL if not already present
 * - Maintains polygon search state during link navigation
 */
const handlePolygonLocation = (url, currentLayout) => {
  if (currentLayout?.id !== LAYOUTS.TYPE_MAP) {
    return url;
  }

  if (typeof window === 'undefined' || !window.location?.search) {
    return url;
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const polygonLocation = searchParams.get(FILTERS.IDS.POLYGON_LOCATION);

    if (polygonLocation && !url.searchParams.has(FILTERS.IDS.POLYGON_LOCATION)) {
      url.searchParams.set(FILTERS.IDS.POLYGON_LOCATION, polygonLocation);
    }

    return url;
  } catch (error) {
    return url;
  }
};

const linkBuildNewHref = ({ href, isInternal, queryParams, keepLayout, currentLayout, initialLayout }) => {
  if (!href) {
    return undefined;
  }

  if (href && !isInternal) {
    return href;
  }

  const newUrl = handlePolygonLocation(
    mergeQueryParams(new URL(href, currentLayout?.url_host), queryParams),
    currentLayout,
  );

  return (keepLayout ? updateLayoutPath(newUrl, currentLayout, initialLayout) : newUrl)?.pathname
    ? (keepLayout ? updateLayoutPath(newUrl, currentLayout, initialLayout) : newUrl).toString()
    : undefined;
};

export default linkBuildNewHref;
